
import { Link } from "react-router-dom";
import { Wrench, Zap, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";

const Engineering = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <Wrench className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            Engineering & Technology
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            Where the principles of science and mathematics are translated into practical applications 
            that shape the modern world—from fundamental structures to cutting-edge innovations.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Main Domains */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Engineering */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Wrench className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Engineering</CardTitle>
                    <CardDescription className="text-lg">
                      Application of scientific principles to design, create, and optimize systems, materials, and technologies
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Core Disciplines</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Civil Engineering: Infrastructure & Environment</li>
                      <li>• Mechanical Engineering: Systems & Manufacturing</li>
                      <li>• Electrical Engineering: Circuits & Electronics</li>
                      <li>• Chemical Engineering: Processes & Materials</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Specialized Fields</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Aerospace Engineering: Flight & Space</li>
                      <li>• Biomedical Engineering: Healthcare Solutions</li>
                      <li>• Environmental Engineering: Sustainability</li>
                      <li>• Materials Engineering: Advanced Composites</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/engineering/core-disciplines">Explore Engineering</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Technology */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Zap className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Technology</CardTitle>
                    <CardDescription className="text-lg">
                      Application of scientific knowledge to solve practical problems and develop innovative solutions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Digital Technologies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Information Technology & Computer Science</li>
                      <li>• Telecommunications & Networking</li>
                      <li>• Robotics & Automation</li>
                      <li>• Nanotechnology & Quantum Computing</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Applied Technologies</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Environmental & Energy Technologies</li>
                      <li>• Consumer Electronics & Devices</li>
                      <li>• Health Technologies & Bioengineering</li>
                      <li>• Advanced Manufacturing & 3D Printing</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/engineering/technology-applications">Explore Technology</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Interdisciplinary & Future */}
            <Card className="group hover:shadow-lg transition-all duration-300 border-2 border-primary/20">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <div className="w-6 h-6 rounded-full bg-primary/50 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Innovation & Future Technologies</CardTitle>
                    <CardDescription className="text-lg">
                      Interdisciplinary fields and emerging technologies shaping tomorrow's solutions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Interdisciplinary Fields</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Cyber-Physical Systems</li>
                      <li>• Advanced Manufacturing & 3D Printing</li>
                      <li>• Health Technologies & Bioengineering</li>
                      <li>• Smart Systems & IoT Integration</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Future Directions</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Quantum Computing & Advanced AI</li>
                      <li>• Sustainable Technology Solutions</li>
                      <li>• Space Technology & Colonization</li>
                      <li>• Ethics in Technology & Innovation</li>
                    </ul>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <Link to="/engineering/innovation-future">Explore Innovation</Link>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Exploring</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/world-itself">Back to The World Itself</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/health-sciences">Health Sciences</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Engineering;
